import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Dashboard from '../pages/Dashboard';
import Login from '../pages/Login';
import BridgePage from '../pages/BridgePage';
import AddApplicant from '../pages/rec/applicant/AddApplicant';
import ApplicantSearch from '../pages/rec/applicant/ApplicantSearch';
import SendMessagePage from '../pages/rec/applicant/SendMessagePage';
import LinkedinSearch from '../pages/rec/linkedin/LinkedinSearch';
import AddVacancy from '../pages/rec/vacancy/AddVacancy';
import EditVacancy from '../pages/rec/vacancy/EditVacancy';
import VacancySearch from '../pages/rec/vacancy/VacancySearch';
import ViewVacancy from '../pages/rec/vacancy/ViewVacancy';
import { PermissionProvider } from '../context/PermissionContext';
import ApplicantProfile from '../pages/rec/applicant/ApplicantProfile';
import UserProfile from '../pages/user/UserProfile';
import type { ApplicantProfileEntryTab } from '../pages/rec/applicant/ApplicantProfile';
import { useAuth } from '../hooks/useAuth';
import { menuService } from '../services/menu.service';
import Loading from '../components/common/Loading';
import { APP_ROUTES } from '../constants/routes';
import { saveReturnUrl } from '../utils/returnUrl';

const routesMap = {
  'ApplicantSearch': { element: <ApplicantSearch />, withPermission: true },
  'VacancySearch': { element: <VacancySearch />, withPermission: true },
  'LinkedinSearch': { element: <LinkedinSearch />, withPermission: true },
  'ApplicantProfile': { element: <ApplicantProfile />, withPermission: true },
  'ApplicantHistory': { element: <ApplicantProfile />, withPermission: true },
  'ApplicantAdditionalInfo': { element: <ApplicantProfile />, withPermission: true },
  'ApplicantDuplicates': { element: <ApplicantProfile />, withPermission: true },
  'ApplicantMessages': { element: <ApplicantProfile />, withPermission: true },
  'AddApplicant': { element: <AddApplicant />, withPermission: true },
  'AddVacancy': { element: <AddVacancy />, withPermission: true },
  'ViewVacancy': { element: <ViewVacancy />, withPermission: true },
  'EditVacancy': { element: <EditVacancy />, withPermission: true },
  'UserProfile': { element: <UserProfile />, withPermission: true },
  'User Profile': { element: <UserProfile />, withPermission: true },
  'SendMessage': { element: <SendMessagePage />, withPermission: true },
}

function getApplicantProfileInitialTab(
  component: keyof typeof routesMap,
): ApplicantProfileEntryTab | undefined {
  switch (component) {
    case 'ApplicantHistory':
      return 'history';
    case 'ApplicantAdditionalInfo':
      return 'additional';
    case 'ApplicantDuplicates':
      return 'duplicates';
    case 'ApplicantMessages':
      return 'messages';
    case 'ApplicantProfile':
    default:
      return undefined;
  }
}

const CatchAllRedirect: React.FC = () => {
  const loc = useLocation();
  console.error('[CatchAllRedirect] path not matched:', loc.pathname + loc.search);
  return <Navigate to={APP_ROUTES.DASHBOARD} replace />;
};

const PermittedRoutes: React.FC<{ userEmail: string | null }> = ({ userEmail }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [routes, setRoutes] = useState<{ component: keyof typeof routesMap, path: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const fetchRoutes = async () => {
      try {
        const response = await menuService.getRoutes<
          { component: keyof typeof routesMap; path: string }[]
        >();
        if (!cancelled) {
          setRoutes(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch routes:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    fetchRoutes();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <React.Fragment>
        <Loading />
      </React.Fragment>
    );
  }

  return (
    <Routes>
      <Route path={APP_ROUTES.DASHBOARD} element={<Dashboard />} />
      <Route
        path={APP_ROUTES.USER.PROFILE}
        element={<PermissionProvider><UserProfile /></PermissionProvider>}
      />
      {/* Static wildcard routes for dynamic paths */}
      <Route
        path="/rec/applicant/search/:id/profile"
        element={
          <PermissionProvider>
            <ApplicantProfile userEmail={userEmail ?? undefined} />
          </PermissionProvider>
        }
      />
      <Route
        path="/rec/vacancy/search/:id/view"
        element={
          <PermissionProvider>
            <ViewVacancy />
          </PermissionProvider>
        }
      />
      <Route
        path="/rec/vacancy/search/:id/edit"
        element={
          <PermissionProvider>
            <EditVacancy />
          </PermissionProvider>
        }
      />
      {routes.map((route) => {
        const config = routesMap[route.component];
        if (!config) return null;
        const initialProfileTab = getApplicantProfileInitialTab(route.component);
        const pageProps = {
          userEmail,
          ...(initialProfileTab !== undefined ? { initialProfileTab } : {}),
        };
        const pageElement = React.cloneElement(config.element, pageProps);
        return (
          <Route
            key={`${route.component}::${route.path}`}
            path={route.path}
            element={
              config.withPermission ? (
                <PermissionProvider>{pageElement}</PermissionProvider>
              ) : (
                pageElement
              )
            }
          />
        );
      })}
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
}

const AppRoutes: React.FC = () => {
  const { isAuthenticated, getActiveUser, msalInstance } = useAuth();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    const user = getActiveUser();
    if (isAuthenticated && user && !userEmail) {
      setUserEmail(user.email);
    }
  }, [isAuthenticated, getActiveUser, userEmail]);

  const searchParams = new URLSearchParams(location.search);
  const isLogoutFlag = searchParams.get('logout') === '1' || searchParams.get('logout') === 'true';

  // Bridge route works for both authenticated and unauthenticated users
  if (location.pathname === APP_ROUTES.BRIDGE) {
    return (
      <Routes>
        <Route path={APP_ROUTES.BRIDGE} element={<BridgePage />} />
      </Routes>
    );
  }

  // Check both isAuthenticated hook and cached accounts for cross-tab compatibility
  const hasCachedAccounts = msalInstance.getAllAccounts().length > 0;
  const effectivelyAuthenticated = isAuthenticated || hasCachedAccounts;

  if (!effectivelyAuthenticated || isLogoutFlag) {
    // Save the intended destination so Login.tsx can redirect there after MSAL succeeds.
    // Skip for logout flows and the root/login page itself.
    if (!isLogoutFlag && location.pathname !== '/') {
      saveReturnUrl(location.pathname + location.search);
    }
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <React.Fragment>
      <PermittedRoutes userEmail={userEmail} />
    </React.Fragment>
  );
};

export default AppRoutes;
